import { useRouteLoaderData } from "react-router"
import { faro } from "@grafana/faro-react"
import { AppInsights } from "@/app-insights"
import { useSharedMedicalRecordEventsStore } from "@/modules/patients/stores/useSharedMedicalRecordEventsStore"
import { SofiaEnvironment } from "@/sofia-environment"
import { Country, UserRole as BackendUserRole } from "@/types/__generated__/union-types"

class AzureAdLoginRedirect extends Error {
  constructor() {
    super("Routing to Azure AD login")

    this.name = "AzureAdLoginRedirect"
  }
}

class MandatorySofiaPreCheckFailed extends Error {
  constructor(helpdeskText: string) {
    super(helpdeskText)

    this.name = "MandatorySofiaPreCheckFailed"
  }
}

const mandatorySofiaPreCheckFailed: (helpdeskText: string) => never = (helpdeskText) => {
  document.body.innerHTML = `<h1 style="color:red;margin:30px 0 0 30px">Unable to proceed. Please report this to the IT-Helpdesk: <br /><span style="color:black">${helpdeskText}</span></h1>`

  throw new MandatorySofiaPreCheckFailed(helpdeskText)
}

const {
  Bff: { Url: bffHost, Credentials: credentials },
} = SofiaEnvironment

export type UserRole =
  | "CallCenterAgent"
  | "CustomerServiceCenter"
  | "NursingProfessional"
  | "Doctor"
  | "MedicalSupervisor"

export type User = {
  objectId: string
  userName: string
  email: string
  familyName: string
  givenName: string
  role: UserRole
  backendRole: BackendUserRole
  countries: Country[]
  session: {
    id: string
    logoutUrl: string
    expiresIn: number
  }
  isOneOf: (roles: UserRole[]) => boolean
  hasMedicalRole: boolean
  hasNonMedicalRole: boolean
}

const NON_MEDICAL_ROLES: UserRole[] = ["CustomerServiceCenter", "CallCenterAgent"]
const MEDICAL_ROLES: UserRole[] = ["MedicalSupervisor", "NursingProfessional", "Doctor"]

const tokenRoleToApplicationRole = (tokenRole: string): UserRole | null => {
  switch (tokenRole) {
    case "Sofia.CCA":
      return "CallCenterAgent"
    case "Sofia.CSC":
      return "CustomerServiceCenter"
    case "Sofia.NursingProfessional":
      return "NursingProfessional"
    case "Sofia.Doctor":
      return "Doctor"
    case "Sofia.MedicalSupervisor":
      return "MedicalSupervisor"
    default:
      return null
  }
}

const tokenCountryToApplicationCountry = (tokenRole: string): Country | null => {
  switch (tokenRole) {
    case "Sofia.CH":
      return "CH"
    case "Sofia.DE":
      return "DE"
    default:
      return null
  }
}
type RawUserPair = {
  type: string
  value: string | number
}

let cachedUser: {
  user: User
  rawUserAttributes: Array<RawUserPair>
} | null = null

const getUser = async () => {
  if (cachedUser) {
    return cachedUser
  }

  const response = await fetch(`${bffHost}/bff/user`, {
    headers: { "X-CSRF": "1" },
    credentials: credentials,
  })

  if (response.status === 401) {
    performLogin()
    throw new AzureAdLoginRedirect()
  }

  if (response.status !== 200) {
    AppInsights.clearAuthenticatedUserContext()
    faro?.api?.resetUser()
    console.log("Failed to get user", response.status)
    mandatorySofiaPreCheckFailed("Failed to get authenticated user information from the backend")
  }

  const userAttributes = (await response.json()) as Array<RawUserPair>

  type RawUser = {
    oid: string
    email: string | null | undefined
    family_name: string | null | undefined
    given_name: string | null | undefined
    sid: string
    "bff:logout_url": string
    "bff:session_expires_in": number
  }

  function isRawValueString(rawValue: string | number): rawValue is string {
    return typeof rawValue === "string"
  }

  function isRole(role: UserRole | null): role is UserRole {
    return !!role
  }

  function isCountry(country: Country | null): country is Country {
    return !!country
  }

  const rawUser: RawUser = Object.fromEntries(userAttributes.map((pair) => [pair.type, pair.value])) as RawUser

  if (!rawUser.email || !rawUser.family_name || !rawUser.given_name) {
    mandatorySofiaPreCheckFailed("The user's claims do not contain a valid email or family name or given name")
  }

  const roles = userAttributes
    .filter((item) => item.type === "roles")
    .map((item) => item.value)
    .filter(isRawValueString)
    .map((value) => tokenRoleToApplicationRole(value))
    .filter(isRole)

  if (roles.length === 0) {
    mandatorySofiaPreCheckFailed("The user does not have any valid Sofia role")
  }

  if (roles.length > 1) {
    const rolesJoined = roles.join(", ")
    mandatorySofiaPreCheckFailed(`The user has more than one Sofia role (${rolesJoined})`)
  }

  const [role] = roles

  const countries: Country[] = userAttributes
    .filter((item) => item.type === "roles")
    .map((item) => item.value)
    .filter(isRawValueString)
    .map((value) => tokenCountryToApplicationCountry(value))
    .filter(isCountry)
    // we don't use variable b as we always want to favor CH
    .sort((a, _b) => (a === "CH" ? -1 : 1)) // always favor CH, so put it first

  if (countries.length === 0) {
    mandatorySofiaPreCheckFailed("The user does not have any valid Sofia country")
  }

  const user: User = {
    objectId: rawUser.oid,
    userName: rawUser.email ?? "unknown@medgate.ch",
    email: rawUser.email ?? "unknown@medgate.ch",
    familyName: rawUser.family_name ?? "Unknown",
    givenName: rawUser.given_name ?? "Unknown",
    role: role,
    countries: countries,
    backendRole: convertBffRoleToUserRole(role),
    session: {
      id: rawUser.sid,
      logoutUrl: rawUser["bff:logout_url"],
      expiresIn: rawUser["bff:session_expires_in"],
    },
    isOneOf: (roles: UserRole[]) => roles.includes(role),
    hasMedicalRole: MEDICAL_ROLES.includes(role),
    hasNonMedicalRole: NON_MEDICAL_ROLES.includes(role),
  }

  console.log("Fetched user information", userAttributes, user)
  AppInsights.setAuthenticatedUserContext(user.userName, user.objectId, true)
  faro?.api?.setUser({
    id: user.objectId,
    username: user.userName,
    attributes: {
      role: user.role,
    },
  })
  cachedUser = {
    user,
    rawUserAttributes: userAttributes,
  }

  return {
    user,
    rawUserAttributes: userAttributes,
  }
}

const performLogin = () => {
  const bffHost = SofiaEnvironment.Bff.Url
  const returnUrl = SofiaEnvironment.Bff.IsDevelopment ? window.location.href : window.location.pathname

  // Record the event before the session is reset in order to keep the metadata
  faro?.api?.pushEvent("session_expired_redirect", { returnUrl })

  AppInsights.clearAuthenticatedUserContext()
  faro?.api?.resetUser()
  window.location.href = `${bffHost}/bff/login?returnUrl=${returnUrl}`
}

const performLogout = (user: User) => {
  useSharedMedicalRecordEventsStore.getState().clearStore()
  const bffHost = SofiaEnvironment.Bff.Url
  const returnUrl = SofiaEnvironment.Bff.IsDevelopment ? window.location.origin : "/"
  window.location.href = `${bffHost}${user.session.logoutUrl}&returnUrl=${returnUrl}`
}

const useUser = () => {
  return useRouteLoaderData("root") as User
}

const convertBffRoleToUserRole = (role: string): BackendUserRole => {
  let userRole: BackendUserRole
  switch (role) {
    case "CallCenterAgent":
      userRole = "CALL_CENTER_AGENT"
      break
    case "MedicalSupervisor":
      userRole = "MEDICAL_SUPERVISOR"
      break
    case "CustomerServiceCenter":
      userRole = "CUSTOMER_SERVICE_CENTER"
      break
    case "NursingProfessional":
      userRole = "NURSING_PROFESSIONAL"
      break
    case "Doctor":
      userRole = "DOCTOR"
      break
    default:
      userRole = "NONE"
      break
  }

  return userRole
}

export { AzureAdLoginRedirect, getUser, useUser, performLogin, performLogout, mandatorySofiaPreCheckFailed }
